<template>
  <section class="dp-layout camera-device-layout">
    <!-- 左侧学校机构树 -->
    <section class="dp-left">
      <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
    </section>

    <!-- 右侧设备列表 -->
    <section class="dp-right glass-card">
      <!-- 工具条 -->
      <toolbar :buttonList="buttonList" @callFunction="callFunction" :searchList="searchList"></toolbar>

      <!-- 设备表格 -->
      <div class="tbl-scroll-host">
      <el-table 
        :data="devices" 
        v-loading="listLoading" 
        @select="dialogCheck" 
        @row-click="selectCurrentRow" 
        ref="table"
        class="custom-tbl modern-table single-select-tbl" 
        height="100%" style="width: 100%"
      >
        <el-table-column type="selection" width="50"></el-table-column>
        <el-table-column prop="DeviceName" label="摄像头名称" min-width="130">
          <template slot-scope="scope">
            <span class="device-name-bold"><i class="el-icon-video-camera"></i> {{ scope.row.DeviceName }}</span>
          </template>
        </el-table-column>
        <el-table-column label="设备标识" min-width="170" class-name="col-device-id">
          <template slot-scope="scope">
            <div class="two-line-cell device-id-cell">
              <span class="primary-line">{{ scope.row.DeviceId || '—' }}</span>
              <span class="secondary-line">
                {{ scope.row.MacAddress ? ('MAC ' + scope.row.MacAddress) : 'MAC：—' }}
                <template v-if="scope.row.CameraIp"> · IP {{ scope.row.CameraIp }}</template>
              </span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="连接" width="110" align="center">
          <template slot-scope="scope">
            <div class="two-line-cell">
              <el-tag v-if="scope.row.IsOnline === true" size="small" type="success" effect="dark">在线</el-tag>
              <el-tag v-else-if="scope.row.IsOnline === false" size="small" type="danger" effect="dark">离线</el-tag>
              <el-tag v-else size="small" type="info" effect="plain"><i class="el-icon-loading"></i> 检测中</el-tag>
              <span class="secondary-line">{{ scope.row.ConnMode === 'Mq' ? '平台直连' : '本地工控机' }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="设备用途" min-width="160" align="center">
          <template slot-scope="scope">
            <div class="two-line-cell">
              <el-tag :type="deviceUsageTagType(scope.row)" size="small" effect="plain">
                {{ deviceUsageLabel(scope.row) }}
              </el-tag>
              <span class="secondary-line" :class="{ 'dorm-bind-missing': isDormBindMissing(scope.row) }">
                {{ attendanceConfigDetail(scope.row) }}
              </span>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="InstallAddress" label="安装位置" min-width="120" show-overflow-tooltip></el-table-column>
        <el-table-column prop="CreateTime" label="录入时间" width="160"></el-table-column>
      </el-table>
      </div>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          layout="total, prev, pager, next, jumper"
          @current-change="handleCurrentChange"
          :page-size="pageSize"
          :total="total"
        ></el-pagination>
      </div>

      <!-- 新增与编辑摄像头对话框 -->
      <el-dialog 
        :title="dialogTitle" 
        :visible.sync="dialogVisible" 
        width="680px" 
        class="modern-dialog camera-edit-dialog"
        :close-on-click-modal="false"
        append-to-body
      >
        <el-form :model="form" label-width="100px" :rules="formRules" ref="form" class="camera-form" size="small">
          <div class="form-section">
            <div class="form-section-title">基本信息</div>
            <el-row :gutter="16">
              <el-col :span="12">
                <el-form-item label="摄像头名称" prop="DeviceName">
                  <el-input v-model="form.DeviceName" placeholder="如：正大门主进" auto-complete="off"></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="设备唯一ID" prop="DeviceId">
                  <el-input v-model="form.DeviceId" placeholder="设备 SN 序列号" auto-complete="off"></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="MAC 地址" prop="MacAddress">
                  <el-input v-model="form.MacAddress" placeholder="可选，如：00:15:18:23:59:BB" auto-complete="off"></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="设备 IP" prop="CameraIp">
                  <el-input
                    v-model="form.CameraIp"
                    :placeholder="form.AttendanceScene === 'VideoOnly' || form.ConnMode === 'LocalEdge' ? '如：192.168.1.15' : '可选，如：192.168.1.15'"
                    auto-complete="off"
                  ></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="24">
                <el-form-item label="安装位置" prop="InstallAddress">
                  <el-input v-model="form.InstallAddress" placeholder="物理安装位置，如：9号楼北门" auto-complete="off"></el-input>
                </el-form-item>
              </el-col>
            </el-row>
          </div>

          <div class="form-section">
            <div class="form-section-title">连接方式</div>
            <el-form-item label="连接模式" prop="ConnMode">
              <el-select
                v-model="form.ConnMode"
                placeholder="请选择连接模式"
                style="width: 100%"
                :disabled="form.AttendanceScene === 'VideoOnly'"
                @change="handleConnModeChange"
              >
                <el-option label="本地工控机" value="LocalEdge"></el-option>
                <el-option label="平台直连" value="Mq" :disabled="form.AttendanceScene === 'VideoOnly'"></el-option>
              </el-select>
            </el-form-item>
            <el-form-item v-if="form.ConnMode === 'LocalEdge'" label="RTSP 地址" prop="RtspUrl">
              <el-input v-model="form.RtspUrl" placeholder="输入设备 IP 后自动生成" auto-complete="off" disabled></el-input>
            </el-form-item>
          </div>

          <div class="form-section">
            <div class="form-section-title">业务用途</div>
            <el-form-item label="用途" prop="AttendanceScene">
              <el-select v-model="form.AttendanceScene" placeholder="请选择设备用途" style="width: 100%" @change="handleSceneChange">
                <el-option label="校门人脸考勤" value="Gate"></el-option>
                <el-option label="宿舍人脸考勤" value="Dorm"></el-option>
                <el-option label="仅联动录像" value="VideoOnly"></el-option>
                <el-option label="暂不参与" value="None"></el-option>
              </el-select>
              <p v-if="form.AttendanceScene === 'VideoOnly'" class="form-help-line">
                <i class="el-icon-info"></i>
                只做刷卡联动录像，无需下发人脸；配好工控机与 IP 后，到考勤终端或宿舍终端设备里绑定即可。
              </p>
              <p v-else-if="form.AttendanceScene === 'Gate' || form.AttendanceScene === 'Dorm'" class="form-help-line">
                <i class="el-icon-info"></i>
                需在「人脸下发同步」中同步人员后才会识别。
              </p>
            </el-form-item>
            <template v-if="form.AttendanceScene === 'Gate' || form.AttendanceScene === 'Dorm'">
              <el-row :gutter="16">
                <el-col :span="12">
                  <el-form-item label="考勤方向" prop="TouchDirection">
                    <el-select v-model="form.TouchDirection" placeholder="请选择考勤方向" style="width: 100%">
                      <el-option label="进出" value="inout" v-if="form.AttendanceScene !== 'Dorm'"></el-option>
                      <el-option label="智能双向" value="auto" v-if="form.AttendanceScene === 'Dorm'"></el-option>
                      <el-option label="进入" value="in"></el-option>
                      <el-option label="离开" value="out"></el-option>
                    </el-select>
                    <p v-if="form.AttendanceScene === 'Dorm' && form.TouchDirection === 'auto'" class="form-help-line">
                      <i class="el-icon-info"></i>
                      根据学生适用的离寝、归寝时间段推断方向，非时段按在寝状态辅助判断。
                    </p>
                  </el-form-item>
                </el-col>
                <el-col :span="12" v-if="form.AttendanceScene === 'Dorm'">
                  <el-form-item label="宿舍楼" prop="DormBuildingId">
                    <el-select v-model="form.DormBuildingId" placeholder="请选择宿舍楼" style="width: 100%">
                      <el-option
                        v-for="building in dormBuildings"
                        :key="building.Id"
                        :label="building.BuildingName || building.Name"
                        :value="building.Id">
                      </el-option>
                    </el-select>
                  </el-form-item>
                </el-col>
              </el-row>
              <el-form-item label="扩展选项">
                <div class="form-switch-row">
                  <span class="switch-item">
                    <el-switch v-model="form.EnableParentPush"></el-switch>
                    <span class="switch-label">家长推送</span>
                  </span>
                  <span v-if="(form.AttendanceScene === 'Gate' || form.AttendanceScene === 'Dorm') && form.ConnMode === 'LocalEdge'" class="switch-item">
                    <el-switch v-model="form.EnableVideoClip"></el-switch>
                    <span class="switch-label">联动录像</span>
                  </span>
                </div>
              </el-form-item>
            </template>
          </div>
        </el-form>
        <div slot="footer" class="dialog-footer">
          <el-button @click.native="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click.native="submitForm" :loading="submitLoading">提交</el-button>
        </div>
      </el-dialog>

      <!-- 摄像头识别历史记录对话框 -->
      <el-dialog  
        title="摄像头识别历史记录" 
        :visible.sync="identifyLogDialogVisible" 
        width="1000px" 
        class="modern-dialog log-dialog"
        :close-on-click-modal="true"
        append-to-body
      >
        <div class="log-toolbar" style="margin-bottom: 15px; display: flex; align-items: center; justify-content: space-between;">
          <div class="search-fields" style="display: flex; gap: 10px; flex-wrap: wrap;">
            <el-input 
              v-model="identifyLogFilters.deviceId" 
              placeholder="设备 SN 序列号" 
              size="small" 
              style="width: 280px;"
              clearable
            ></el-input>
            <el-input 
              v-model="identifyLogFilters.name" 
              placeholder="人员姓名 / ID" 
              size="small" 
              style="width: 200px;"
              clearable
            ></el-input>
            <el-button type="primary" size="small" icon="el-icon-search" @click="searchIdentifyLogs">查询</el-button>
            <el-button size="small" icon="el-icon-refresh" @click="resetIdentifyLogFilters">重置</el-button>
          </div>
          <div style="display: flex; gap: 8px;">
            <el-button
              type="danger"
              size="small"
              icon="el-icon-delete"
              :disabled="!identifyLogSelection.length"
              :loading="identifyLogDeleteLoading"
              @click="batchDeleteIdentifyLogs"
            >批量删除{{ identifyLogSelection.length ? `（${identifyLogSelection.length}）` : '' }}</el-button>
            <el-button
              type="danger"
              plain
              size="small"
              icon="el-icon-delete-solid"
              :disabled="!identifyLogTotal"
              :loading="identifyLogClearLoading"
              @click="clearIdentifyLogs"
            >清空</el-button>
          </div>
        </div>

        <el-table 
          ref="identifyLogTable"
          :data="identifyLogs" 
          v-loading="identifyLogLoading" 
          class="custom-tbl modern-table identify-log-table" 
          style="width: 100%"
          height="420"
          @row-click="handleIdentifyLogRowClick"
          @selection-change="handleIdentifyLogSelectionChange"
        >
          <el-table-column type="selection" width="45" align="center"></el-table-column>
          <el-table-column prop="DeviceName" label="摄像头名称" min-width="120"></el-table-column>
          <el-table-column prop="DeviceId" label="设备唯一ID (SN)" min-width="120"></el-table-column>
          <el-table-column prop="PersonName" label="姓名" width="100"></el-table-column>
          <el-table-column prop="PersonType" label="人员类型" width="100">
            <template slot-scope="scope">
              <el-tag size="small" :type="scope.row.PersonType === 'student' ? 'primary' : 'success'">
                {{ scope.row.PersonType === 'student' ? '学生' : (scope.row.PersonType === 'teacher' ? '教职工' : '未知') }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="PhotoUrl" label="抓拍图" width="100" align="center">
            <template slot-scope="scope">
              <oss-media-thumbs
                v-if="scope.row.PhotoUrl"
                :media="scope.row.PhotoUrl"
                :school-id="scope.row.SchoolId || DepartmentId"
                thumb-width="50px"
                thumb-height="50px"
                :max="1"
              />
              <span v-else>—</span>
            </template>
          </el-table-column>
          <el-table-column prop="Confidence" label="置信度" width="90" align="center">
            <template slot-scope="scope">
              <span v-if="scope.row.Confidence !== null && scope.row.Confidence !== undefined">{{ (scope.row.Confidence * 100).toFixed(1) }}%</span>
              <span v-else>—</span>
            </template>
          </el-table-column>
          <el-table-column prop="IsAttendance" label="考勤处理" width="100" align="center">
            <template slot-scope="scope">
              <el-tag size="small" :type="scope.row.IsAttendance ? 'success' : 'info'" effect="dark">
                {{ scope.row.IsAttendance ? '已考勤' : '仅记录' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="IdentifyTime" label="识别时间" width="160"></el-table-column>
        </el-table>

        <!-- 识别历史分页 -->
        <div class="pagination-container">
          <el-pagination
            layout="total, sizes, prev, pager, next"
            :page-sizes="[10, 20, 50, 100]"
            @size-change="handleIdentifyLogSizeChange"
            @current-change="handleIdentifyLogPageChange"
            :current-page="identifyLogPage"
            :page-size="identifyLogPageSize"
            :total="identifyLogTotal"
          ></el-pagination>
        </div>
      </el-dialog>
    </section>
  </section>
</template>

<script>
import axios from "axios";
import { getTeachingDepartmentTree } from '../../api/modules/department';
import { getBuildingsListPage } from '../../api/modules/dormitory';
import { BaseApiUrl } from '../../api/base';
import { probeDevicePhysical } from '../../api/modules/integration';
import { getButtonList } from "../../promissionRouter";
import Toolbar from "../../components/Toolbar";
import DepartmentPane from "../../components/DepartmentPane";

export default {
  components: { Toolbar, DepartmentPane },
  data() {
    return {
      filters: {},
      searchList: [
        {
          label: "设备名称 / 序列号",
          key: "name",
        },
        {
          label: "设备用途",
          key: "attendanceScene",
          type: "select",
          options: [
            { label: "全部", value: "" },
            { label: "校门人脸考勤", value: "Gate" },
            { label: "宿舍人脸考勤", value: "Dorm" },
            { label: "仅联动录像", value: "VideoOnly" },
            { label: "暂不参与", value: "None" }
          ]
        },
        {
          label: "连接模式",
          key: "connMode",
          type: "select",
          options: [
            { label: "全部", value: "" },
            { label: "本地工控机", value: "LocalEdge" },
            { label: "平台直连", value: "Mq" }
          ]
        },
      ],
      devices: [],
      options: [],
      total: 0,
      page: 1,
      pageSize: 10,
      listLoading: false,
      buttonList: [],
      currentRow: null,
      dialogVisible: false,
      submitLoading: false,
      dialogTitle: "新增摄像头设备",
      isResourceShow: 0,
      DepartmentId: 0,

      // 识别历史日志弹窗所需参数
      identifyLogDialogVisible: false,
      identifyLogLoading: false,
      identifyLogDeleteLoading: false,
      identifyLogClearLoading: false,
      identifyLogs: [],
      identifyLogSelection: [],
      identifyLogTotal: 0,
      identifyLogPage: 1,
      identifyLogPageSize: 10,
      identifyLogFilters: {
        deviceId: "",
        name: ""
      },
      mqProbeStarted: {},
      dormBuildings: [],
      
      form: {
        Id: 0,
        DeviceName: "",
        DeviceId: "",
        MacAddress: "",
        CameraIp: "",
        RtspUrl: "",
        InstallAddress: "",
        SchoolId: "",
        Dids: [],
        IsAttendanceEnabled: false,
        AttendanceScene: "Gate",
        ConnMode: "LocalEdge",
        TouchDirection: "inout",
        DormBuildingId: null,
        EnableVideoClip: true,
        EnableParentPush: true
      },
      
      formRules: {
        DeviceName: [
          { required: true, message: "请输入摄像头名称", trigger: "blur" }
        ],
        DeviceId: [
          { required: true, message: "请输入设备唯一ID (SN)", trigger: "blur" }
        ],
        CameraIp: [
          { validator: (rule, value, callback) => {
              // 工控机 / 仅联动录像必填；平台直连可选。最后更新：2026-10-26
              const needIp = this.form.AttendanceScene === "VideoOnly" || this.form.ConnMode === "LocalEdge";
              if (needIp && !value) return callback(new Error("请输入设备 IP"));
              if (value && !/^((2(5[0-5]|[0-4]\d))|[0-1]?\d{1,2})(\.((2(5[0-5]|[0-4]\d))|[0-1]?\d{1,2})){3}$/.test(value)) {
                return callback(new Error("请输入有效的 IP 地址"));
              }
              callback();
            }, trigger: "blur" }
        ],
        AttendanceScene: [
          { required: true, message: "请选择设备用途", trigger: "change" }
        ],
        ConnMode: [
          { required: true, message: "请选择连接模式", trigger: "change" }
        ],
        TouchDirection: [
          { validator: (rule, value, callback) => {
              if ((this.form.AttendanceScene === "Gate" || this.form.AttendanceScene === "Dorm") && !value) {
                return callback(new Error("请选择考勤方向"));
              }
              callback();
            }, trigger: "change" }
        ],
        DormBuildingId: [
          { validator: (rule, value, callback) => {
              if (this.form.AttendanceScene === "Dorm" && !value) return callback(new Error("宿舍考勤请选择宿舍楼"));
              callback();
            }, trigger: "change" }
        ]
      }
    };
  },
  methods: {
    treeNodeClick(data, node, component) {
      this.DepartmentId = data.value;
      this.mqProbeStarted = {};
      this.page = 1;
      this.handleQuery();
    },
    
    dialogCheck(selection, row) {
      this.currentRow = null;
      this.$refs.table.clearSelection();
      if (selection.length === 0) {
        return;
      }
      if (row) {
        this.selectCurrentRow(row);
      }
    },
    
    selectCurrentRow(val) {
      if (val) {
        this.currentRow = val;
        this.$refs.table.clearSelection();
        this.$refs.table.toggleRowSelection(val, true);
      }
    },
    
    callFunction(item) {
      if (typeof item.search === "object") {
        this.filters = item.search;
      } else {
        this.filters = {
          name: item.search,
        };
      }
      this[item.Func].apply(this, item);
    },
    
    handleCurrentChange(val) {
      this.page = val;
      this.handleQuery();
    },
    
    // 获取摄像头设备列表
    handleQuery() {
      this.listLoading = true;
      const params = {
        page: this.page,
        schoolId: this.DepartmentId || "",
        name: this.filters.name || "",
        attendanceScene: this.filters.attendanceScene || "",
        connMode: this.filters.connMode || "",
        intPageSize: this.pageSize
      };
      
      axios.get(`${BaseApiUrl}/api/KqtCameraDevice/Get`, { params })
        .then((res) => {
          this.listLoading = false;
          if (res.data && res.data.success) {
            this.total = res.data.response.dataCount;
            const devicesData = res.data.response.data || [];
            const previousStatus = {};
            this.devices.forEach((device) => {
              if (device && device.DeviceId && device.IsOnline !== undefined) {
                previousStatus[device.DeviceId] = device.IsOnline;
              }
            });
            devicesData.forEach(d => {
              this.$set(
                d,
                'IsOnline',
                previousStatus[d.DeviceId] !== undefined
                  ? previousStatus[d.DeviceId]
                  : undefined
              );
            });
            this.devices = devicesData;
            this.loadDormBuildings();
            this.loadOnlineStatus();
          } else {
            this.$message.error(res.data.msg || "获取列表失败");
          }
        })
        .catch((err) => {
          this.listLoading = false;
          this.$message.error("网络异常，获取列表失败");
        });
    },

    /** 考勤方向中文。最后更新：2026-10-25 */
    touchDirectionLabel(dir) {
      if (dir === "in") return "进入";
      if (dir === "out") return "离开";
      if (dir === "inout") return "进出";
      if (dir === "auto") return "智能双向";
      return "";
    },

    /** 列表用途主标签。最后更新：2026-10-26 */
    deviceUsageLabel(row) {
      if (!row) return "未参与";
      if (row.AttendanceScene === "VideoOnly") return "仅录像";
      if (row.AttendanceScene === "Dorm") return "宿舍";
      if (row.AttendanceScene === "Gate") return "校门";
      if (row.IsAttendanceEnabled === true) return "已开启";
      return "未参与";
    },

    deviceUsageTagType(row) {
      if (!row) return "info";
      if (row.AttendanceScene === "VideoOnly") return "warning";
      if (row.AttendanceScene === "Dorm" || row.AttendanceScene === "Gate") return "success";
      return "info";
    },

    /** 宿舍场景未选楼栋 */
    isDormBindMissing(row) {
      return !!row
        && row.AttendanceScene === "Dorm"
        && !(row.DormBuildingId > 0);
    },

    /**
     * 列表用途副文案。最后更新：2026-10-26
     */
    attendanceConfigDetail(row) {
      if (!row) return "";
      if (row.AttendanceScene === "VideoOnly") return "刷卡联动剪辑 · 无需下发";
      if (row.AttendanceScene === "None" || (row.IsAttendanceEnabled !== true && row.AttendanceScene !== "Gate" && row.AttendanceScene !== "Dorm")) {
        return "不参与考勤";
      }
      const dir = this.touchDirectionLabel(row.TouchDirection);
      if (row.AttendanceScene === "Dorm") {
        if (!(row.DormBuildingId > 0)) return "未绑楼栋";
        const building = this.dormBuildingName(row.DormBuildingId);
        return dir ? (building + " · " + dir) : building;
      }
      return dir || "人脸考勤";
    },

    dormBuildingName(buildingId) {
      if (!buildingId) return "";
      const hit = (this.dormBuildings || []).find((b) => Number(b.Id) === Number(buildingId));
      return (hit && (hit.BuildingName || hit.Name)) || ("楼栋#" + buildingId);
    },
    
    // 按连接模式检测设备状态：工控机读取旧在线列表，MQ 发送一次 queryPersonStatistics。
    loadOnlineStatus() {
      if (!this.devices || this.devices.length === 0) return;
      const localEdgeDevices = this.devices.filter(d => d.ConnMode !== "Mq");
      const mqDevices = this.devices.filter(d => d.ConnMode === "Mq");

      if (localEdgeDevices.length > 0) {
        axios.get(`${BaseApiUrl}/api/KqtCameraDevice/GetOnlineDeviceIds`)
          .then((res) => {
            if (res.data && res.data.success) {
              const onlineIds = res.data.response || [];
              localEdgeDevices.forEach((d) => {
                this.$set(d, 'IsOnline', onlineIds.includes(d.DeviceId));
              });
            }
          })
          .catch((err) => {
            console.error("获取工控机在线状态失败", err);
            localEdgeDevices.forEach((d) => this.$set(d, 'IsOnline', false));
          });
      }

      // 每台 MQ 设备打开页面时只探测一次，避免把 MQ 查询混入旧 WebSocket 状态接口。
      mqDevices.forEach((device) => {
        if (this.mqProbeStarted[device.DeviceId]) return;
        this.$set(this.mqProbeStarted, device.DeviceId, true);
        this.probeMqDevice(device);
      });
    },

    // 通过摄像头协议查询人脸总数，判断 MQ 设备当前是否可通信。
    probeMqDevice(device) {
      probeDevicePhysical({
        // 与同步页面保持一致，使用当前左侧选中的学校主键，避免设备记录中的
        // SchoolId 可能是 sch_xxx 标识而导致 API 查询不到设备。
        schoolId: this.DepartmentId ? String(this.DepartmentId) : device.SchoolId,
        deviceId: device.DeviceId,
        action: "query_person_stats"
      })
        .then((res) => {
          const body = res && res.data;
          if (!body || !body.success || !body.response) {
            this.$set(device, "IsOnline", false);
            return;
          }

          let response = body.response;
          if (typeof response === "string") {
            try {
              response = JSON.parse(response);
            } catch (error) {
              response = null;
            }
          }
          const responseStatus = response
            && (response.status !== undefined ? response.status : response.Status);
          const isOnline = Number(responseStatus) === 0;
          // 列表可能在请求期间被重新加载，不能依赖旧对象引用。
          const deviceIndex = this.devices.findIndex(
            currentDevice => currentDevice.DeviceId === device.DeviceId
          );
          if (deviceIndex >= 0) {
            this.$set(this.devices, deviceIndex, Object.assign({}, device, {
              IsOnline: isOnline
            }));
          } else {
            this.$set(device, "IsOnline", isOnline);
          }
        })
        .catch(() => {
          this.$set(device, "IsOnline", false);
        });
    },
    
    // 刷新设备列表与网关在线状态（清空搜索框以进行全量更新）
    handleRefresh() {
      this.filters = {};
      this.page = 1;
      this.mqProbeStarted = {};
      this.handleQuery();
    },

    // 清理设备页面离开后仍在等待的 MQ 状态轮询。
    clearMqStatusTimers() {
      // 单次长等待由 API 管理，页面离开时不再创建新的轮询请求。
    },
    
    // 显示新增界面
    handleAdd() {
      if (!this.DepartmentId || this.DepartmentId <= 0) {
        this.$message.warning("请先在左侧选择所属学校，再进行摄像头设备录入！");
        return;
      }
      this.dialogTitle = "新增摄像头设备";
      this.dialogVisible = true;
      
      // 初始化表单数据
      this.form = {
        Id: 0,
        DeviceName: "",
        DeviceId: "",
        MacAddress: "",
        CameraIp: "",
        RtspUrl: "",
        InstallAddress: "",
        SchoolId: "",
        Dids: [],
        IsAttendanceEnabled: false,
        AttendanceScene: "VideoOnly",
        ConnMode: "LocalEdge",
        TouchDirection: "inout",
        DormBuildingId: null,
        EnableVideoClip: true,
        EnableParentPush: false
      };
      this.loadDormBuildings();
      
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate();
        }
      });
    },
    
    // 显示编辑界面
    handleEdit() {
      const row = this.currentRow;
      if (!row) {
        this.$message.error("请选择要编辑的一行数据！");
        return;
      }
      
      this.dialogTitle = "编辑摄像头配置";
      this.dialogVisible = true;
      
      // 深度拷贝对象
      this.form = Object.assign({}, row);
      // 旧数据：未开考勤且无用途时，按暂不参与展示
      if (!this.form.AttendanceScene) {
        this.form.AttendanceScene = this.form.IsAttendanceEnabled === true ? "Gate" : "None";
      }
      this.form.ConnMode = this.form.ConnMode || "LocalEdge";
      this.form.TouchDirection = this.form.TouchDirection || (this.form.AttendanceScene === "Dorm" ? "in" : "inout");
      this.form.EnableVideoClip = this.form.EnableVideoClip == null ? true : this.form.EnableVideoClip;
      this.form.EnableParentPush = this.form.EnableParentPush == null
        ? this.form.AttendanceScene === "Gate"
        : this.form.EnableParentPush === true;
      if (this.form.AttendanceScene === "VideoOnly") {
        this.form.ConnMode = "LocalEdge";
        this.form.EnableVideoClip = true;
      }
      this.syncRtspFromIp();
      this.loadDormBuildings();
      
      this.$nextTick(() => {
        if (this.$refs.form) {
          this.$refs.form.clearValidate();
        }
      });
    },

    handleSceneChange(scene) {
      if (scene === "VideoOnly") {
        this.form.ConnMode = "LocalEdge";
        this.form.IsAttendanceEnabled = false;
        this.form.EnableVideoClip = true;
        this.form.EnableParentPush = false;
        this.form.DormBuildingId = null;
        this.syncRtspFromIp();
      } else if (scene === "None") {
        this.form.IsAttendanceEnabled = false;
        this.form.EnableVideoClip = false;
        this.form.EnableParentPush = false;
      } else if (scene === "Dorm") {
        this.form.IsAttendanceEnabled = true;
        if (this.form.TouchDirection === "inout") this.form.TouchDirection = "in";
        // 宿舍人脸 + 工控默认开联动录像；MQ 关。最后更新：2026-11-01
        this.form.EnableVideoClip = this.form.ConnMode === "LocalEdge";
        this.loadDormBuildings();
      } else if (scene === "Gate") {
        this.form.IsAttendanceEnabled = true;
        if (!this.form.TouchDirection) this.form.TouchDirection = "inout";
        if (this.form.ConnMode === "LocalEdge") {
          this.form.EnableVideoClip = this.form.EnableVideoClip == null ? true : this.form.EnableVideoClip;
        } else {
          this.form.EnableVideoClip = false;
        }
      }
    },

    /**
     * 按设备 IP 生成/清空 RTSP（工控机）。切换连接模式后也要重算。最后更新：2026-10-26
     */
    syncRtspFromIp() {
      if (this.form.ConnMode === "Mq") {
        this.form.RtspUrl = "";
        return;
      }
      const ip = (this.form.CameraIp || "").trim();
      this.form.RtspUrl = ip
        ? `rtsp://admin:admin123@${ip}:554/ch0/stream0`
        : "";
    },

    // 切换连接模式：直连清 RTSP；切回工控机按 IP 重算并默认开录像。最后更新：2026-11-01
    handleConnModeChange(mode) {
      if (mode === "Mq") {
        this.form.RtspUrl = "";
        this.form.EnableVideoClip = false;
        return;
      }
      this.syncRtspFromIp();
      if (this.form.AttendanceScene === "Gate" || this.form.AttendanceScene === "Dorm") {
        this.form.EnableVideoClip = true;
      }
    },

    loadDormBuildings() {
      if (!this.DepartmentId) {
        this.dormBuildings = [];
        return;
      }
      getBuildingsListPage({ page: 1, intPageSize: 500, departmentId: this.DepartmentId })
        .then((res) => {
          this.dormBuildings = (res.data && res.data.response && res.data.response.data) || [];
        })
        .catch(() => {
          this.dormBuildings = [];
        });
    },
    
    // 加载机构树级联选项（按顶部固定学校 scope，避免总平台/其他学校）。最后更新：2026-08-29
    loadDepartmentTree() {
      const schoolId = this.$store.state.activeSchoolId
        || window.localStorage.getItem('activeSchoolId')
        || this.DepartmentId;
      if (!schoolId || Number(schoolId) <= 0) {
        this.options = [];
        this.$message.warning("请先选择学校");
        return;
      }
      const para = { pid: 0, type: 1, scopeDepartmentId: schoolId };
      getTeachingDepartmentTree(para).then((res) => {
        this.isResourceShow++;
        const root = res.data && res.data.response;
        this.options = root ? [root] : [];
      });
    },
    
    // 删除摄像头
    handleDel() {
      const row = this.currentRow;
      if (!row) {
        this.$message.error("请选择要删除的一行数据！");
        return;
      }
      
      this.$confirm("确认物理删除该摄像头设备配置吗？摄像头、人脸同步映射及宿舍绑定将被永久删除，识别日志会保留。", "提示", {
        type: "warning"
      }).then(() => {
        this.listLoading = true;
        axios.delete(`${BaseApiUrl}/api/KqtCameraDevice/Delete?id=${row.Id}`)
          .then((res) => {
            this.listLoading = false;
            if (res.data && res.data.success) {
              this.$message.success("设备删除成功");
              this.handleQuery();
            } else {
              this.$message.error(res.data.msg || "删除失败");
            }
          })
          .catch((err) => {
            this.listLoading = false;
            this.$message.error("网络异常，删除失败");
          });
      }).catch(() => {});
    },
    
    // 提交新增/修改
    submitForm() {
      this.$refs.form.validate((valid) => {
        if (valid) {
          this.$confirm("确认提交吗？", "提示", {}).then(() => {
            this.submitLoading = true;
            
            // 组装参数
            const para = Object.assign({}, this.form);
            // 自动继承当前左侧所选学校ID或原设备所属学校ID
            para.SchoolId = para.Id > 0 ? para.SchoolId.toString() : this.DepartmentId.toString();
            // 用途驱动是否人脸考勤（后端仍会再规范化一次）
            para.IsAttendanceEnabled = para.AttendanceScene === "Gate" || para.AttendanceScene === "Dorm";
            if (para.AttendanceScene === "VideoOnly") {
              para.ConnMode = "LocalEdge";
              para.EnableVideoClip = true;
              para.EnableParentPush = false;
              para.DormBuildingId = null;
            }
            
            const req = para.Id > 0 
              ? axios.put(`${BaseApiUrl}/api/KqtCameraDevice/Put`, para)
              : axios.post(`${BaseApiUrl}/api/KqtCameraDevice/Post`, para);
              
            req.then((res) => {
              this.submitLoading = false;
              if (res.data && res.data.success) {
                this.$message.success(res.data.msg || "提交成功");
                this.dialogVisible = false;
                this.handleQuery();
              } else {
                this.$message.error(res.data.msg || "提交失败");
              }
            }).catch((err) => {
              this.submitLoading = false;
              this.$message.error("网络异常，提交失败");
            });
          });
        }
      });
    },

    // 打开识别记录弹窗并初始化数据
    handleIdentifyLog() {
      const row = this.currentRow;
      this.identifyLogDialogVisible = true;
      this.identifyLogFilters.deviceId = row ? row.DeviceId : "";
      this.identifyLogFilters.name = "";
      this.identifyLogPage = 1;
      this.identifyLogSelection = [];
      this.handleIdentifyLogQuery();
    },

    // 分页查询识别抓拍历史记录
    handleIdentifyLogQuery() {
      this.identifyLogLoading = true;
      this.identifyLogSelection = [];
      const params = {
        page: this.identifyLogPage,
        schoolId: this.DepartmentId || "",
        deviceId: this.identifyLogFilters.deviceId || "",
        name: this.identifyLogFilters.name || "",
        intPageSize: this.identifyLogPageSize
      };
      
      axios.get(`${BaseApiUrl}/api/KqtCameraDevice/GetIdentifyLogs`, { params })
        .then((res) => {
          this.identifyLogLoading = false;
          if (res.data && res.data.success) {
            this.identifyLogTotal = res.data.response.dataCount;
            this.identifyLogs = res.data.response.data || [];
          } else {
            this.$message.error(res.data.msg || "获取历史记录失败");
          }
        })
        .catch((err) => {
          this.identifyLogLoading = false;
          this.$message.error("网络异常，获取历史记录失败");
        });
    },

    // 触发搜索
    searchIdentifyLogs() {
      this.identifyLogPage = 1;
      this.handleIdentifyLogQuery();
    },

    // 重置搜索条件并重新加载
    resetIdentifyLogFilters() {
      this.identifyLogFilters.deviceId = "";
      this.identifyLogFilters.name = "";
      this.identifyLogPage = 1;
      this.handleIdentifyLogQuery();
    },

    // 日志翻页
    handleIdentifyLogPageChange(val) {
      this.identifyLogPage = val;
      this.handleIdentifyLogQuery();
    },

    /** 切换每页条数。最后更新：2026-10-25 */
    handleIdentifyLogSizeChange(val) {
      this.identifyLogPageSize = val;
      this.identifyLogPage = 1;
      this.handleIdentifyLogQuery();
    },

    /**
     * 点击行切换勾选（点复选框/抓拍图预览时不重复切换）。最后更新：2026-10-25
     */
    handleIdentifyLogRowClick(row, column, event) {
      if (!row || !this.$refs.identifyLogTable) return;
      const target = (event && event.target) || null;
      if (target && (target.closest(".el-checkbox") || target.closest(".el-image"))) return;
      this.$refs.identifyLogTable.toggleRowSelection(row);
    },

    /** 识别历史多选变更 */
    handleIdentifyLogSelectionChange(rows) {
      this.identifyLogSelection = rows || [];
    },

    /** 批量删除勾选的识别历史记录 */
    batchDeleteIdentifyLogs() {
      const ids = (this.identifyLogSelection || [])
        .map((row) => row.Id)
        .filter((id) => id != null && id !== "");
      if (!ids.length) {
        this.$message.warning("请先勾选要删除的记录");
        return;
      }
      this.$confirm(`确认删除选中的 ${ids.length} 条识别记录吗？`, "批量删除", {
        type: "warning",
        confirmButtonText: "删除",
        cancelButtonText: "取消"
      }).then(() => {
        this.identifyLogDeleteLoading = true;
        axios.post(`${BaseApiUrl}/api/KqtCameraDevice/BatchDeleteIdentifyLogs`, ids)
          .then((res) => {
            this.identifyLogDeleteLoading = false;
            if (res.data && res.data.success) {
              this.$message.success(res.data.msg || "删除成功");
              this.identifyLogSelection = [];
              this.handleIdentifyLogQuery();
            } else {
              this.$message.error((res.data && res.data.msg) || "批量删除失败");
            }
          })
          .catch(() => {
            this.identifyLogDeleteLoading = false;
            this.$message.error("网络异常，批量删除失败");
          });
      }).catch(() => {});
    },

    /**
     * 按当前筛选范围清空识别记录（有设备 SN 则清该设备，否则清本校全部）。最后更新：2026-10-25
     */
    clearIdentifyLogs() {
      if (!this.DepartmentId) {
        this.$message.warning("请先选择学校");
        return;
      }
      if (!this.identifyLogTotal) {
        this.$message.info("没有可清空的记录");
        return;
      }
      const deviceId = (this.identifyLogFilters.deviceId || "").trim();
      const scopeText = deviceId
        ? `设备 ${deviceId} 的全部识别记录（约 ${this.identifyLogTotal} 条）`
        : `本校全部识别记录（约 ${this.identifyLogTotal} 条）`;
      this.$confirm(`确认清空${scopeText}吗？此操作不可恢复。`, "清空识别记录", {
        type: "warning",
        confirmButtonText: "清空",
        cancelButtonText: "取消"
      }).then(() => {
        this.identifyLogClearLoading = true;
        axios.post(`${BaseApiUrl}/api/KqtCameraDevice/ClearIdentifyLogs`, null, {
          params: {
            schoolId: this.DepartmentId,
            deviceId: deviceId || ""
          }
        }).then((res) => {
          this.identifyLogClearLoading = false;
          if (res.data && res.data.success) {
            this.$message.success(res.data.msg || "清空成功");
            this.identifyLogSelection = [];
            this.identifyLogPage = 1;
            this.handleIdentifyLogQuery();
          } else {
            this.$message.error((res.data && res.data.msg) || "清空失败");
          }
        }).catch(() => {
          this.identifyLogClearLoading = false;
          this.$message.error("网络异常，清空失败");
        });
      }).catch(() => {});
    },

  },
  watch: {
    'form.CameraIp'() {
      this.syncRtspFromIp();
    }
  },
  mounted() {
    let routers = window.localStorage.router
      ? JSON.parse(window.localStorage.router)
      : [];
    this.buttonList = getButtonList(this.$route.path, routers);
    
    // 兜底逻辑：若后台权限管理中未对该页面配置按钮，自动加载默认按钮组，保障界面可用性
    if (!this.buttonList || this.buttonList.length === 0) {
      this.buttonList = [
        { name: "查询", Func: "handleQuery", id: 9901, IsHide: false, icon: "el-icon-search", btnType: "primary" },
        { name: "新增", Func: "handleAdd", id: 9902, IsHide: false, icon: "el-icon-plus", btnType: "success" },
        { name: "编辑", Func: "handleEdit", id: 9903, IsHide: false, icon: "el-icon-edit", btnType: "primary" },
        { name: "删除", Func: "handleDel", id: 9904, IsHide: false, icon: "el-icon-delete", btnType: "danger" }
      ];
    }
    
    // 自动追加一个“刷新”按钮，以便操作员一键同步摄像头在网关中的最新在线状态
    if (this.buttonList && this.buttonList.length > 0) {
      if (!this.buttonList.some(btn => btn.Func === "handleRefresh")) {
        this.buttonList.push({
          name: "刷新",
          Func: "handleRefresh",
          id: 9905,
          IsHide: false,
          icon: "el-icon-refresh",
          btnType: "warning"
        });
      }
    }

    // 自动追加一个“识别记录”按钮，用于一键查看全部或当前摄像头的识别抓拍日志
    if (this.buttonList && this.buttonList.length > 0) {
      if (!this.buttonList.some(btn => btn.Func === "handleIdentifyLog")) {
        this.buttonList.push({
          name: "识别记录",
          Func: "handleIdentifyLog",
          id: 9906,
          IsHide: false,
          icon: "el-icon-tickets",
          btnType: "info"
        });
      }
    }
  },
  deactivated() {
    this.clearMqStatusTimers();
    this.mqProbeStarted = {};
  },
  beforeDestroy() {
    this.clearMqStatusTimers();
    this.mqProbeStarted = {};
  }
};
</script>

<style scoped>
.log-dialog /deep/ .el-dialog__body {
  padding: 15px 20px 10px 20px;
}
/* 识别记录表固定高度，数据少时也撑满，避免分页上蹿下跳 */
.identify-log-table {
  margin-top: 0;
}
/* 仅设备主列表单选：隐藏表头全选；识别记录弹窗需保留全选 */
.single-select-tbl /deep/ .has-gutter .el-checkbox {
  display: none;
}
.pagination-container {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  box-sizing: border-box;
  margin-top: 12px;
  padding: 0 8px 4px 0;
}
.camera-device-layout {
  padding: 12px;
}
.glass-card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}
.modern-table {
  margin-top: 15px;
}
/* 行内各列垂直居中，避免双行内容顶到格子上方 */
.modern-table /deep/ .el-table__body td {
  vertical-align: middle;
}
.modern-table /deep/ .col-device-id .cell {
  display: flex;
  align-items: center;
}
.device-name-bold {
  font-weight: 600;
  color: #2c3e50;
}
.device-name-bold i {
  color: var(--btn-primary, #2563eb);
  margin-right: 4px;
}
.ip-tag {
  font-family: Consolas, Monaco, monospace;
}
.two-line-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  line-height: 1.25;
}
.two-line-cell.device-id-cell {
  align-items: flex-start;
  width: 100%;
}
.primary-line {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Consolas, Monaco, monospace;
}
.secondary-line {
  color: #909399;
  font-size: 12px;
  white-space: nowrap;
}
.dorm-bind-missing {
  color: #e6a23c;
}
.toolbar-pagination {
  margin-top: 15px;
}
.modern-dialog /deep/ .el-dialog {
  border-radius: 12px;
  overflow: hidden;
}
.modern-dialog /deep/ .el-dialog__header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 15px 20px;
}
.modern-dialog /deep/ .el-dialog__title {
  color: #2c3e50;
  font-weight: bold;
}
.camera-edit-dialog /deep/ .el-dialog__body {
  padding: 12px 20px 4px;
  max-height: 70vh;
  overflow-y: auto;
}
.camera-form {
  padding: 0 4px 8px;
}
.form-section {
  margin-bottom: 14px;
  padding: 12px 14px 4px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 8px;
}
.form-section-title {
  margin: 0 0 10px;
  padding: 0 0 0 10px;
  border-left: 3px solid var(--btn-primary, #2563eb);
  color: #303133;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
}
.form-help-line {
  margin: 2px 0 0;
  padding: 0;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}
.form-help-line i {
  margin-right: 4px;
  color: #909399;
}
.form-switch-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 20px;
  min-height: 32px;
}
.switch-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.switch-label {
  color: #606266;
  font-size: 13px;
}
</style>
